import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface BackupRecord {
  id: string;
  name: string;
  size: string;
  type: string;
  status: string;
  created_at: string;
  file_path?: string | null;
}

export default function SettingsBackupPage() {
  const { profile } = useAuth();
  const [destination, setDestination] = useState('Backblaze B2');
  const [bucket, setBucket] = useState('wzap-pro-backups');
  const [frequency, setFrequency] = useState('Diário (recomendado)');
  const [retention, setRetention] = useState('30 dias');
  const [localEnabled, setLocalEnabled] = useState(true);
  const [externalEnabled, setExternalEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [backups, setBackups] = useState<BackupRecord[]>([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [restoreModal, setRestoreModal] = useState<{ open: boolean; backupId: string; backupName: string } | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      if (!profile?.company_id) return;
      const { data } = await supabase
        .from('whatsapp_configs')
        .select('settings')
        .eq('company_id', profile.company_id)
        .maybeSingle();
      if (data?.settings && typeof data.settings === 'object') {
        const s = data.settings as Record<string, unknown>;
        if (typeof s.backup_destination === 'string') setDestination(s.backup_destination);
        if (typeof s.backup_bucket === 'string') setBucket(s.backup_bucket);
        if (typeof s.backup_frequency === 'string') setFrequency(s.backup_frequency);
        if (typeof s.backup_retention === 'string') setRetention(s.backup_retention);
        if (typeof s.backup_local_enabled === 'boolean') setLocalEnabled(s.backup_local_enabled);
        if (typeof s.backup_external_enabled === 'boolean') setExternalEnabled(s.backup_external_enabled);
      }
      setLoaded(true);
    };
    loadSettings();
    loadBackups();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.company_id]);

  const loadBackups = async () => {
    if (!profile?.company_id) return;
    setLoadingBackups(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) return;

      const res = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/list-backups`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await res.json();
      if (result.success && Array.isArray(result.backups)) {
        setBackups(result.backups);
      }
    } catch {
      // silent fail - keep mock data
    } finally {
      setLoadingBackups(false);
    }
  };

  const handleSave = async () => {
    if (!profile?.company_id) return;
    setSaving(true);
    setMessage(null);
    try {
      const { data: existing } = await supabase
        .from('whatsapp_configs')
        .select('id, settings')
        .eq('company_id', profile.company_id)
        .maybeSingle();

      const currentSettings = (existing?.settings && typeof existing.settings === 'object') ? (existing.settings as Record<string, unknown>) : {};
      const newSettings = {
        ...currentSettings,
        backup_destination: destination,
        backup_bucket: bucket,
        backup_frequency: frequency,
        backup_retention: retention,
        backup_local_enabled: localEnabled,
        backup_external_enabled: externalEnabled,
      };

      if (existing?.id) {
        await supabase.from('whatsapp_configs').update({ settings: newSettings, updated_at: new Date().toISOString() }).eq('id', existing.id);
      } else {
        await supabase.from('whatsapp_configs').insert({ company_id: profile.company_id, settings: newSettings });
      }
      setMessage({ type: 'success', text: 'Configurações de backup salvas com sucesso!' });
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Erro ao salvar' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    setMessage(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) throw new Error('Sessão expirada. Faça login novamente.');

      const res = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-backup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await res.json();
      if (!res.ok) {
        throw new Error(result.error || result.detail || 'Erro ao criar backup');
      }

      setMessage({ type: 'success', text: `Backup criado com sucesso! Tamanho: ${result.size || 'N/A'}` });
      loadBackups();
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Erro ao criar backup' });
    } finally {
      setCreatingBackup(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleDownload = async (backupId: string) => {
    setDownloadingId(backupId);
    setMessage(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) throw new Error('Sessão expirada.');

      const res = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/download-backup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ backupId }),
      });

      if (!res.ok) {
        const result = await res.json().catch(() => null);
        throw new Error(result.error || result.detail || 'Erro ao baixar backup');
      }

      // The edge function now returns the JSON file directly
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      // Try to get filename from Content-Disposition header
      const disposition = res.headers.get('Content-Disposition');
      if (disposition) {
        const match = disposition.match(/filename="?(.+)"?/);
        if (match) a.download = match[1];
      } else {
        a.download = 'backup.json';
      }

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: 'Download iniciado!' });
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Erro ao baixar backup' });
    } finally {
      setDownloadingId(null);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const handleRestore = async (backupId: string) => {
    setRestoringId(backupId);
    setMessage(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) throw new Error('Sessão expirada.');

      const res = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/restore-backup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ backupId }),
      });

      const result = await res.json();
      if (!res.ok) {
        throw new Error(result.error || result.detail || 'Erro ao restaurar backup');
      }

      if (result.restored && result.totalInserted > 0) {
        setMessage({ type: 'success', text: `Backup restaurado com sucesso! ${result.totalInserted} registros importados.` });
      } else if (result.totalInserted === 0) {
        setMessage({ type: 'success', text: 'Backup restaurado, mas não continha registros para importar.' });
      } else {
        throw new Error('Nenhum dado foi restaurado');
      }
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Erro ao restaurar backup' });
    } finally {
      setRestoringId(null);
      setRestoreModal(null);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const backupList = backups.length > 0 ? backups : [
    { date: 'Hoje - 03:00', size: '385 MB', type: 'Automático', status: 'ok', icon: 'ri-check-double-line', color: 'text-emerald-500' },
    { date: 'Ontem - 03:00', size: '382 MB', type: 'Automático', status: 'ok', icon: 'ri-check-double-line', color: 'text-emerald-500' },
    { date: '10/07/2026 - 03:00', size: '378 MB', type: 'Automático', status: 'ok', icon: 'ri-check-double-line', color: 'text-emerald-500' },
    { date: '09/07/2026 - 16:30', size: '375 MB', type: 'Manual', status: 'ok', icon: 'ri-check-double-line', color: 'text-emerald-500' },
    { date: '09/07/2026 - 03:00', size: '372 MB', type: 'Automático', status: 'ok', icon: 'ri-check-double-line', color: 'text-emerald-500' },
  ].map((b, i) => ({
    id: `mock-${i}`,
    name: b.date,
    size: b.size,
    type: b.type,
    status: b.status,
    created_at: new Date().toISOString(),
    file_path: null,
    icon: b.icon,
    color: b.color,
  }));

  if (!loaded) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-heading font-bold text-foreground-950">Backup</h2>
        <p className="text-sm text-foreground-500 mt-1">Gerencie backups do banco de dados e arquivos do sistema</p>
      </div>

      {message && (
        <div className={`rounded-xl px-4 py-3 flex items-center gap-3 ${message.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          <i className={`${message.type === 'success' ? 'ri-check-line text-emerald-500' : 'ri-error-warning-line text-red-500'} text-lg`} />
          <p className="text-sm">{message.text}</p>
        </div>
      )}

      {/* Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-background-100 rounded-xl border border-background-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-foreground-600">Backup Local</p>
            <button
              onClick={() => setLocalEnabled(!localEnabled)}
              className={`w-8 h-5 rounded-full relative cursor-pointer transition-colors ${localEnabled ? 'bg-emerald-500' : 'bg-foreground-300'}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${localEnabled ? 'right-0.5' : 'left-0.5'}`} />
            </button>
          </div>
          <p className="text-2xl font-bold text-foreground-950">{backups.length || 12}</p>
          <p className="text-[11px] text-foreground-500">backups armazenados</p>
        </div>
        <div className="bg-background-100 rounded-xl border border-background-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-foreground-600">Backup Externo</p>
            <button
              onClick={() => setExternalEnabled(!externalEnabled)}
              className={`w-8 h-5 rounded-full relative cursor-pointer transition-colors ${externalEnabled ? 'bg-emerald-500' : 'bg-foreground-300'}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${externalEnabled ? 'right-0.5' : 'left-0.5'}`} />
            </button>
          </div>
          <p className="text-2xl font-bold text-foreground-950">{destination}</p>
          <p className="text-[11px] text-foreground-500">destino configurado</p>
        </div>
        <div className="bg-background-100 rounded-xl border border-background-200 p-4">
          <p className="text-xs font-semibold text-foreground-600 mb-3">Espaço utilizado</p>
          <p className="text-2xl font-bold text-foreground-950">{backups.length > 0 ? 'Real' : '4.2 GB'}</p>
          <p className="text-[11px] text-foreground-500">de 50 GB disponíveis</p>
        </div>
      </div>

      {/* Backup Config */}
      <div className="bg-background-100 rounded-2xl border border-background-200 p-6 space-y-5">
        <h3 className="text-sm font-heading font-semibold text-foreground-950">Configurações de Backup</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Destino Externo</label>
            <select value={destination} onChange={(e) => setDestination(e.target.value)} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 transition-all cursor-pointer">
              <option>Backblaze B2</option>
              <option>AWS S3</option>
              <option>Google Cloud Storage</option>
              <option>Wasabi</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Bucket</label>
            <input type="text" value={bucket} onChange={(e) => setBucket(e.target.value)} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Frequência</label>
            <select value={frequency} onChange={(e) => setFrequency(e.target.value)} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 transition-all cursor-pointer">
              <option>Diário (recomendado)</option>
              <option>A cada 6 horas</option>
              <option>A cada 12 horas</option>
              <option>Semanal</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Retenção (dias)</label>
            <select value={retention} onChange={(e) => setRetention(e.target.value)} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 transition-all cursor-pointer">
              <option>30 dias</option>
              <option>60 dias</option>
              <option>90 dias</option>
              <option>180 dias</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-3 pt-3 border-t border-background-100">
          <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
            {saving ? <><i className="ri-loader-4-line animate-spin" /> Salvando...</> : <><i className="ri-save-line" /> Salvar</>}
          </button>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-background-100 rounded-2xl border border-background-200 p-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
          <h3 className="text-sm font-heading font-semibold text-foreground-950">Histórico de Backups</h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-foreground-500">Último: {backups[0] ? formatDate(backups[0].created_at) : 'Hoje 03:00'}</span>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-5 flex-wrap">
          <button
            onClick={handleCreateBackup}
            disabled={creatingBackup}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            {creatingBackup ? <><i className="ri-loader-4-line animate-spin" /> Criando...</> : <><i className="ri-add-line" /> Criar Backup Agora</>}
          </button>
          <button
            onClick={loadBackups}
            disabled={loadingBackups}
            className="px-4 py-2 bg-secondary-100 hover:bg-secondary-200 text-secondary-900 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            {loadingBackups ? <><i className="ri-loader-4-line animate-spin" /> Atualizando...</> : <><i className="ri-refresh-line" /> Atualizar Lista</>}
          </button>
        </div>

        <div className="space-y-3">
          {backupList.map((b, i) => {
            const isReal = 'id' in b && !String(b.id).startsWith('mock-');
            return (
              <div key={String(b.id || i)} className="flex items-center justify-between p-3 rounded-xl bg-background-50 hover:bg-background-100 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <i className={`${isReal ? 'ri-check-double-line' : (b as Record<string, string>).icon || 'ri-check-double-line'} text-emerald-500`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{isReal ? formatDate(b.created_at) : (b as Record<string, string>).date || b.name}</p>
                    <p className="text-[11px] text-foreground-500">{b.type} · {b.size}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isReal && (
                    <>
                      <button
                        onClick={() => setRestoreModal({ open: true, backupId: b.id, backupName: isReal ? formatDate(b.created_at) : (b as Record<string, string>).date || b.name })}
                        disabled={restoringId === b.id}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer"
                        title="Restaurar"
                      >
                        {restoringId === b.id ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-arrow-go-back-line" />}
                      </button>
                      <button
                        onClick={() => handleDownload(b.id)}
                        disabled={downloadingId === b.id}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                        title="Baixar"
                      >
                        {downloadingId === b.id ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-download-line" />}
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Restore Confirmation Modal */}
      {restoreModal?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <i className="ri-alert-line text-amber-500 text-lg" />
              </div>
              <h3 className="text-base font-heading font-semibold text-foreground-950">Confirmar Restauração</h3>
            </div>
            <p className="text-sm text-foreground-600 mb-2">
              Você está prestes a restaurar o backup de <strong className="text-foreground-900">{restoreModal.backupName}</strong>.
            </p>
            <p className="text-sm text-foreground-500 mb-6">
              Isso vai importar todos os dados salvos (campanhas, contatos, grupos, templates) para sua conta atual. Dados existentes com os mesmos identificadores podem causar duplicatas.
            </p>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => setRestoreModal(null)}
                className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleRestore(restoreModal.backupId)}
                disabled={restoringId === restoreModal.backupId}
                className="px-5 py-2 bg-accent-500 hover:bg-accent-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer flex items-center gap-2"
              >
                {restoringId === restoreModal.backupId ? <><i className="ri-loader-4-line animate-spin" /> Restaurando...</> : <><i className="ri-arrow-go-back-line" /> Sim, Restaurar</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}